import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, from, shareReplay, firstValueFrom, throwError } from 'rxjs';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { TokenStorageService } from './token-storage.service';
import { tap, catchError, retry, delay } from 'rxjs/operators';
import { LoggerService } from './logger.service';

interface StripeAccountStatus {
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  private apiUrl = environment.apiUrl;
  private stripeConnectSubject = new BehaviorSubject<any>(null);
  private initializationPromise: Promise<any> | null = null;
  private isRefreshingSession = false;
  private refreshPromise: Promise<any> | null = null;

  // Exposed observable that components can subscribe to
  readonly stripeConnect$ = this.stripeConnectSubject.asObservable().pipe(shareReplay(1));

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
    private logger: LoggerService,
  ) {
    this.setupGlobalErrorHandler();
  }

  /**
   * Set up global error handler for Stripe session expiration errors
   */
  private setupGlobalErrorHandler() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleGlobalStripeError(event.reason);
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      this.handleGlobalStripeError(event.error);
    });
  }

  /**
   * Handle global Stripe-related errors, particularly session expiration
   */
  private async handleGlobalStripeError(error: any) {
    if (!error || this.isRefreshingSession) {
      return;
    }

    const errorMessage = error?.message || error?.toString() || '';

    // Check if this is a Stripe session expiration error
    if (this.isSessionExpirationError(errorMessage)) {
      this.logger.log(
        '🔄 [StripeService] Global error handler detected Stripe session expiration, attempting to refresh...',
      );

      try {
        await this.handleSessionExpiration();
        this.logger.log(
          '✅ [StripeService] Global error handler successfully refreshed Stripe session',
        );
      } catch (refreshError) {
        this.logger.error(
          '❌ [StripeService] Global error handler failed to refresh Stripe session:',
          refreshError,
        );
      }
    }
  }

  createAccountSession(): Observable<{ client_secret: string }> {
    const stripeAccountId = this.tokenStorage.getCompanyStripeAccountId();
    this.logger.log('🏦 [StripeService] Retrieved stripe account ID:', stripeAccountId);

    const requestBody = { account: stripeAccountId };

    this.logger.log('📤 [StripeService] Requesting Stripe account session:', {
      url: `${this.apiUrl}/stripe-account-session`,
      method: 'POST',
      stripeAccountId,
      requestBody,
    });

    return this.http
      .post<{
        client_secret: string;
      }>(`${this.apiUrl}/stripe-account-session`, requestBody)
      .pipe(
        tap((response) => {
          this.logger.log('✅ [StripeService] Stripe account session created successfully:', {
            hasClientSecret: !!response.client_secret,
            clientSecretLength: response.client_secret ? response.client_secret.length : 0,
          });
        }),
        catchError((error) => {
          this.logger.error('❌ [StripeService] Failed to create Stripe account session:', {
            error: error.message || error,
            status: error.status,
            statusText: error.statusText,
            url: error.url,
          });

          // Provide more specific error messages
          if (
            error.status === 400 &&
            error.error?.detail?.includes('Stripe account ID is required')
          ) {
            throw new Error(
              'Bitte vervollständigen Sie zuerst Ihr Profil, um Stripe zu konfigurieren.',
            );
          }

          throw error;
        }),
      );
  }

  getAccountStatus(accountId: string): Observable<StripeAccountStatus> {
    return this.http.get<StripeAccountStatus>(`${this.apiUrl}/stripe-account-status/${accountId}`);
  }

  /**
   * Initialize Stripe Connect with automatic session refresh handling
   */
  initializeStripeConnect(): Observable<any> {
    this.logger.log('🚀 [StripeService] initializeStripeConnect() called');

    // If already initialized, return the existing observable
    if (this.stripeConnectSubject.value) {
      this.logger.log(
        '✅ [StripeService] Stripe Connect already initialized, returning existing instance',
      );
      return this.stripeConnect$;
    }

    // If initialization is in progress, return the existing promise as observable
    if (this.initializationPromise) {
      this.logger.log(
        '⏳ [StripeService] Stripe Connect initialization already in progress, returning existing promise',
      );
      return from(this.initializationPromise);
    }

    this.logger.log('🔄 [StripeService] Starting Stripe Connect initialization');

    // Start initialization
    this.initializationPromise = this.initializeStripeConnectInternal();

    // Convert promise to observable and return
    return from(this.initializationPromise);
  }

  /**
   * Force refresh of Stripe Connect (useful when session expires)
   */
  refreshStripeConnect(): Observable<any> {
    this.logger.log('🔄 [StripeService] refreshStripeConnect() called');

    // Clear existing state
    this.stripeConnectSubject.next(null);
    this.initializationPromise = null;

    // Start fresh initialization
    this.initializationPromise = this.initializeStripeConnectInternal();

    return from(this.initializationPromise);
  }

  /**
   * Check if Stripe Connect is currently initialized
   */
  isStripeConnectInitialized(): boolean {
    return this.stripeConnectSubject.value !== null;
  }

  /**
   * Get the current Stripe Connect instance if available
   */
  getCurrentStripeConnect(): any {
    return this.stripeConnectSubject.value;
  }

  /**
   * Check if Stripe can be initialized (has account ID and profile completed)
   */
  canInitializeStripe(): boolean {
    const stripeAccountId = this.tokenStorage.getCompanyStripeAccountId();
    const isProfileCompleted = this.tokenStorage.getCompanyIsProfileCompleted();

    return !!(stripeAccountId && isProfileCompleted);
  }

  /**
   * Get the reason why Stripe cannot be initialized
   */
  getStripeInitializationError(): string | null {
    const stripeAccountId = this.tokenStorage.getCompanyStripeAccountId();
    const isProfileCompleted = this.tokenStorage.getCompanyIsProfileCompleted();

    if (!isProfileCompleted) {
      return 'Bitte vervollständigen Sie zuerst Ihr Profil, um Stripe zu konfigurieren.';
    }

    if (!stripeAccountId) {
      return 'Stripe-Konto wurde noch nicht erstellt. Bitte vervollständigen Sie Ihr Profil.';
    }

    return null;
  }

  /**
   * Handle account session expiration by refreshing the session
   */
  private async handleSessionExpiration(): Promise<any> {
    if (this.isRefreshingSession) {
      this.logger.log('⏳ [StripeService] Session refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    this.logger.log('🔄 [StripeService] Handling account session expiration, refreshing...');
    this.isRefreshingSession = true;

    try {
      // Clear existing state
      this.stripeConnectSubject.next(null);
      this.initializationPromise = null;

      // Wait a bit before retrying to avoid rapid requests
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Start fresh initialization
      this.initializationPromise = this.initializeStripeConnectInternal();
      const result = await this.initializationPromise;

      this.logger.log('✅ [StripeService] Session refreshed successfully');
      return result;
    } catch (error) {
      this.logger.error('❌ [StripeService] Failed to refresh session:', error);
      throw error;
    } finally {
      this.isRefreshingSession = false;
      this.refreshPromise = null;
    }
  }

  private async initializeStripeConnectInternal(): Promise<any> {
    try {
      this.logger.log('🔧 [StripeService] Internal initialization started');

      // Check if we have a Stripe account ID before attempting to initialize
      const stripeAccountId = this.tokenStorage.getCompanyStripeAccountId();
      if (!stripeAccountId) {
        throw new Error('Stripe account ID not found. Please complete your profile first.');
      }

      const fetchClientSecret = async () => {
        this.logger.log('🔑 [StripeService] Fetching client secret for Stripe Connect');
        const response = await firstValueFrom(this.createAccountSession());
        if (!response) {
          throw new Error('Failed to get account session');
        }
        this.logger.log('🔑 [StripeService] Client secret retrieved successfully');
        return response.client_secret;
      };

      this.logger.log(
        '⚙️ [StripeService] Loading Stripe Connect with publishable key:',
        environment.stripePublishableKey,
      );

      const stripeConnect = await loadConnectAndInitialize({
        publishableKey: environment.stripePublishableKey,
        fetchClientSecret,
        appearance: {
          overlays: 'dialog',
          variables: {
            colorPrimary: '#4b5ae4',
          },
        },
      });

      this.logger.log('✅ [StripeService] Stripe Connect loaded and initialized successfully');

      this.stripeConnectSubject.next(stripeConnect);
      return stripeConnect;
    } catch (error) {
      this.logger.error('❌ [StripeService] Failed to initialize Stripe Connect:', error);

      // Check if this is a session expiration error
      if (this.isSessionExpirationError(error)) {
        this.logger.log('🔄 [StripeService] Detected session expiration, attempting to refresh...');
        try {
          return await this.handleSessionExpiration();
        } catch (refreshError) {
          this.logger.error(
            '❌ [StripeService] Failed to refresh session after expiration:',
            refreshError,
          );
          this.stripeConnectSubject.error(refreshError);
          this.initializationPromise = null;
          throw refreshError;
        }
      }

      this.stripeConnectSubject.error(error);
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * Check if the error indicates a session expiration
   */
  private isSessionExpirationError(error: any): boolean {
    const errorMessage = error?.message || error?.toString() || '';

    // Check for common Stripe session expiration error messages
    const sessionExpirationPatterns = [
      'Failed to claim account session',
      'account session is invalid',
      'account session is expired',
      'account session has been used',
      'session expired',
      'invalid session',
      'UILayerMessagePort',
      'claimresultupdate',
      'network error',
      'api error',
    ];

    return sessionExpirationPatterns.some((pattern) =>
      errorMessage.toLowerCase().includes(pattern.toLowerCase()),
    );
  }

  /**
   * Create a Stripe component with automatic retry on session expiration
   */
  async createStripeComponent(componentType: string, retryCount: number = 0): Promise<any> {
    try {
      // Validate that Stripe can be initialized before attempting to create components
      if (!this.canInitializeStripe()) {
        const errorMessage = this.getStripeInitializationError();
        this.logger.error(
          `❌ [StripeService] Cannot create ${componentType} component:`,
          errorMessage,
        );
        throw new Error(errorMessage || 'Stripe cannot be initialized');
      }

      const stripeConnect = await firstValueFrom(this.initializeStripeConnect());
      return stripeConnect.create(componentType);
    } catch (error) {
      this.logger.error(`❌ [StripeService] Failed to create ${componentType} component:`, error);

      if (this.isSessionExpirationError(error) && retryCount < 2) {
        this.logger.log(
          `🔄 [StripeService] Retrying component creation after session refresh (attempt ${retryCount + 1})`,
        );
        try {
          await this.handleSessionExpiration();
          return this.createStripeComponent(componentType, retryCount + 1);
        } catch (refreshError) {
          this.logger.error(
            `❌ [StripeService] Failed to refresh session for ${componentType}:`,
            refreshError,
          );
          throw refreshError;
        }
      }

      // If it's not a session expiration error or we've exhausted retries, throw the original error
      throw error;
    }
  }
}
